import './Input.css';
import React, { useState, forwardRef, useId, useRef, ReactNode, ReactElement } from 'react';
import { PhoneInput, type PhoneInputRefType } from 'react-international-phone';
import 'react-international-phone/style.css';
import { CustomSelect } from '../Select/Select';
import { clsx } from 'clsx';
import { MultiSelect } from 'src/components/MultiSelect/MultiSelect';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { InvalidMessage } from 'src/components/Input/InvalidMessage';

interface PasswordEyeProps {
  onClick?: () => void;
  readonly?: boolean;
}

const PasswordEye = ({ onClick, readonly }: PasswordEyeProps) => {
  const [visible, setVisible] = useState(false);
  const toggleEye = () => {
    if (readonly) {
      return;
    }
    setVisible(!visible);
    onClick?.();
  };

  return (
    <button
      type="button"
      aria-label={visible ? 'Hide password' : 'Show password'}
      onClick={toggleEye}
      className={clsx(visible ? 'visible eye' : 'eye', 'z-40')}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <circle cx="10" cy="10" r="5" fill="#75F3FB" />
        <path
          d="M9.99968 3.75C5.83301 3.75 2.27467 6.34167 0.833008 10C2.27467 13.6583 5.83301 16.25 9.99968 16.25C14.1663 16.25 17.7247 13.6583 19.1663 10C17.7247 6.34167 14.1663 3.75 9.99968 3.75ZM9.99968 14.1667C7.69968 14.1667 5.83301 12.3 5.83301 10C5.83301 7.7 7.69968 5.83333 9.99968 5.83333C12.2997 5.83333 14.1663 7.7 14.1663 10C14.1663 12.3 12.2997 14.1667 9.99968 14.1667ZM9.99968 7.5C8.61634 7.5 7.49968 8.61667 7.49968 10C7.49968 11.3833 8.61634 12.5 9.99968 12.5C11.383 12.5 12.4997 11.3833 12.4997 10C12.4997 8.61667 11.383 7.5 9.99968 7.5Z"
          fill="#05050D"
        />
      </svg>
    </button>
  );
};

type InputType =
  | 'text'
  | 'password'
  | 'email'
  | 'tel'
  | 'number'
  | 'phone'
  | 'textarea'
  | 'select'
  | 'multi-select';

interface InfoTooltip {
  side?: 'top' | 'right' | 'bottom' | 'left';
  description: string | ReactElement;
}

// The selects match options by label; `value` is optional extra data.
export interface InputSelectOption {
  label: string;
  value?: string | number;
  [key: string]: unknown;
}

interface BaseInputProps {
  placeholder?: string;
  onBlur?: React.FocusEventHandler;
  onFocus?: React.FocusEventHandler;
  onKeyUp?: React.KeyboardEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  isValid?: boolean;
  label?: string;
  labelClassName?: string;
  invalidMessage?: string | ReactNode;
  icon?: ReactNode;
  iconAlign?: 'left' | 'right';
  readonly?: boolean;
  autoFocus?: boolean;
  selectOptions?: InputSelectOption[];
  inputmode?: string;
  pattern?: string;
  autocomplete?: string;
  maxlength?: number;
  dropdownClassName?: string;
  dropdownMenuClassNames?: string;
  inputBlockClassName?: string;
  prefix?: ReactNode;
  className?: string;
  textareaRows?: number;
  textareaFieldSizing?: 'fixed' | 'content';
  textareaResizable?: boolean;
  infoTooltip?: InfoTooltip | null;
  customLabel?: string;
  onClick?: React.MouseEventHandler;
}

// What `value` holds and what `onChange` receives depend on the kind of field.
interface TextInputProps extends BaseInputProps {
  type?: Exclude<InputType, 'phone' | 'select' | 'multi-select'>;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClose?: never;
}

interface PhoneInputProps extends BaseInputProps {
  type: 'phone';
  value?: string;
  onChange?: (phone: string) => void;
  onClose?: never;
}

interface SelectInputProps extends BaseInputProps {
  type: 'select';
  // Matched to an option by label.
  value?: { label?: string } | null;
  onChange?: (option: InputSelectOption) => void;
  onClose?: never;
}

interface MultiSelectInputProps extends BaseInputProps {
  type: 'multi-select';
  value?: InputSelectOption[];
  onChange?: (selected: InputSelectOption[], option: InputSelectOption, checked: boolean) => void;
  onClose?: (selected: InputSelectOption[]) => void;
}

export type InputProps =
  TextInputProps | PhoneInputProps | SelectInputProps | MultiSelectInputProps;

// The component branches on `type` itself, so internally it reads every variant through
// one looser shape; the public union above is what callers are checked against.
type InputImplProps = BaseInputProps & {
  type?: InputType;
  value?: unknown;
  onChange?: (...args: unknown[]) => void;
  onClose?: (...args: unknown[]) => void;
};

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement | null, InputProps>(
  (props, ref) => {
    const {
      type,
      placeholder,
      value,
      onChange,
      onBlur,
      onFocus,
      onClose,
      onKeyUp = () => {},
      onKeyDown = () => {},
      isValid,
      label,
      labelClassName,
      invalidMessage,
      icon,
      iconAlign,
      readonly = false,
      autoFocus,
      selectOptions = [],
      inputmode,
      pattern,
      autocomplete,
      maxlength,
      dropdownClassName = '',
      dropdownMenuClassNames = '',
      inputBlockClassName = '',
      prefix,
      className,
      textareaRows = 4,
      textareaFieldSizing = 'content',
      textareaResizable = false,
      infoTooltip = null,
      customLabel = '',
    } = props as InputImplProps;
    const fallbackRef = useRef<HTMLInputElement | null>(null);
    const resolvedRef = ref || fallbackRef;
    const isPassword = type === 'password';
    const [inputType, setInputType] = useState(type);
    // Stable across renders, so the label stays tied to the same field.
    const fieldId = useId();
    const id = label ? `${label.replace(/\s+/g, '-').toLowerCase()}-${fieldId}` : fieldId;
    const togglePassword = () => {
      if (readonly) {
        return;
      }
      setInputType(inputType === 'password' ? 'text' : 'password');
    };

    // For the select types the forwarded ref ends up on the select's container div.
    const selectRef = resolvedRef as unknown as React.Ref<HTMLDivElement>;
    // For the phone type it ends up on the phone input, an <input> with the library's extra API.
    const phoneRef = resolvedRef as React.Ref<PhoneInputRefType>;
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [textareaHeight, setTextareaHeight] = useState<number | null>(null);

    const setTextareaRef = (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof resolvedRef === 'function') {
        (resolvedRef as (instance: HTMLTextAreaElement | null) => void)(node);
      } else if (resolvedRef) {
        (resolvedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    };

    const handleResizeStart = (event: React.PointerEvent<HTMLSpanElement>) => {
      if (readonly || !textareaRef.current) {
        return;
      }
      event.preventDefault();
      const startY = event.clientY;
      const startHeight = textareaRef.current.offsetHeight;
      const MIN_TEXTAREA_HEIGHT = 40;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const nextHeight = Math.max(MIN_TEXTAREA_HEIGHT, startHeight + moveEvent.clientY - startY);
        setTextareaHeight(nextHeight);
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    };

    return (
      <React.Fragment>
        {label ? (
          <label
            htmlFor={`${id}`}
            className={clsx('input-label flex flex-row gap-[5px] items-center', labelClassName)}
          >
            <span>{label}</span>
            {infoTooltip && <InputInfoToolTip infoToolTip={infoTooltip} />}
          </label>
        ) : null}
        <div
          className={clsx(
            'input-block flex items-center',
            inputBlockClassName,
            iconAlign ? `icon-align-${iconAlign}` : 'icon-align-default',
          )}
        >
          {icon && iconAlign === 'left' && icon}
          {type === 'phone' && (
            <PhoneInput
              ref={phoneRef}
              defaultCountry="us"
              className={clsx('input', isValid ? 'valid' : 'invalid', className)}
              placeholder={placeholder}
              value={value as string}
              onChange={(phone: string) => onChange?.(phone)}
              onBlur={onBlur}
              disabled={readonly}
              inputProps={{ id, onKeyUp, onKeyDown, autoFocus }}
              onFocus={onFocus}
            />
          )}
          {type === 'textarea' && (
            <div className="textarea-wrapper">
              <textarea
                id={id}
                ref={setTextareaRef}
                className={clsx('input', isValid ? 'valid' : 'invalid', className)}
                placeholder={placeholder}
                value={value as string | number}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyUp={onKeyUp}
                onKeyDown={onKeyDown}
                readOnly={readonly}
                autoFocus={autoFocus}
                rows={textareaRows}
                style={
                  {
                    fieldSizing: textareaResizable ? 'fixed' : textareaFieldSizing,
                    ...(textareaHeight !== null ? { height: `${textareaHeight}px` } : {}),
                  } as React.CSSProperties
                }
              />
              {textareaResizable && !readonly && (
                <span
                  className="textarea-resize-handle"
                  onPointerDown={handleResizeStart}
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.5254 15L15.0003 12.5251"
                      stroke="#AEB0B6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8.81348 13.7626L13.7627 8.81339"
                      stroke="#AEB0B6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M4.18262 13.4446L13.375 4.25219"
                      stroke="#AEB0B6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              )}
            </div>
          )}
          {(type === 'text' ||
            type === 'password' ||
            type === 'email' ||
            type === 'tel' ||
            type === 'number') && (
            <>
              {prefix && (
                <span className="absolute md:left-2.5 left-2 max-md:text-sm font-medium">
                  {prefix}
                </span>
              )}
              <input
                ref={resolvedRef as React.Ref<HTMLInputElement>}
                id={id}
                className={clsx('input', isValid ? 'valid' : 'invalid', className)}
                type={inputType}
                inputMode={inputmode as React.HTMLAttributes<HTMLInputElement>['inputMode']}
                pattern={pattern}
                autoComplete={autocomplete}
                maxLength={maxlength}
                placeholder={placeholder}
                value={value as string | number}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyUp={onKeyUp}
                onKeyDown={onKeyDown}
                readOnly={readonly}
                autoFocus={autoFocus}
              />
            </>
          )}
          {type === 'select' && (
            <CustomSelect
              ref={selectRef}
              label={label}
              customLabel={customLabel}
              className={dropdownClassName}
              dropdownMenuClassNames={dropdownMenuClassNames}
              options={selectOptions}
              value={value}
              onChange={onChange}
              readonly={readonly}
            />
          )}
          {type === 'multi-select' && (
            <MultiSelect
              ref={selectRef}
              dropdownMenuClassNames={dropdownMenuClassNames}
              options={selectOptions}
              values={value as InputSelectOption[]}
              onChange={onChange}
              onClose={onClose}
            />
          )}
          {!isValid && invalidMessage ? (
            <InvalidMessage invalidMessage={invalidMessage} isPassword={isPassword} />
          ) : null}
          {isPassword ? <PasswordEye onClick={togglePassword} readonly={readonly} /> : null}
          {((icon && !iconAlign) || (icon && iconAlign === 'right')) && icon}
        </div>
      </React.Fragment>
    );
  },
);

Input.displayName = 'Input';

export default Input;
